import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import AspectFit from "../../../components/video/AspectFit";
import CameraGrid, { GRID_LAYOUTS, type GridLayout } from "../../../components/video/CameraGrid";
import { CAMERA_DRAG_MIME, type CameraTileSource } from "../../../components/video/CameraTile";
import { useUserStore } from "../../../store/userStore";
import { useAcList, useAcMutations, useCameraStream, type AcCamera, type AcView } from "../api/accessControlApi";
import {
  CAMERA_ICON,
  CAMERA_TONE,
  DetailRow,
  LoadState,
  SITE_ICON,
  SiteTree,
  StatusChip,
  ToolButton,
  relativeTime,
  toneOf,
  useTicker,
  type TreeGroup,
} from "../components/console/Console";

// ===========================================================================
// Seguridad y vigilancia → Cámaras: «Vista principal» al estilo iVMS-4200.
// Árbol de sitios y cámaras (arrastrables) + vistas guardadas a la izquierda,
// mosaico 1/4/8/12 al centro (se recuerda por usuario), paginación cuando hay
// más cámaras que celdas y la ficha de la cámara seleccionada a la derecha.
// ===========================================================================

const STORAGE_PREFIX = "nuvexAccess.cameraWall.";


interface StoredPrefs {
  layout: GridLayout;
}

export function cameraWallStorageKey(userId: string | null | undefined): string {
  return `${STORAGE_PREFIX}${userId || "anon"}`;
}

export function readWallPrefs(key: string): StoredPrefs {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? (JSON.parse(raw) as Partial<StoredPrefs>) : {};
    const layout = GRID_LAYOUTS.includes(parsed.layout as GridLayout) ? (parsed.layout as GridLayout) : 4;
    return { layout };
  } catch {
    return { layout: 4 };
  }
}

function writeWallPrefs(key: string, prefs: StoredPrefs) {
  try {
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    /* almacenamiento bloqueado: la preferencia vive sólo en la sesión */
  }
}

export const toTileSource = (c: AcCamera): CameraTileSource => ({
  id: c.id,
  name: c.name,
  siteName: c.site_name,
  status: c.status,
  thumbnailUrl: c.thumbnail_url,
  snapshotUrl: c.snapshot_url,
  // La URL del stream no viene en la lista: se pide aparte para la celda maximizada.
  streamUrl: null,
  audio: c.audio,
  lastSeenAt: c.last_seen_at,
});

export default function CameraWall() {
  const { t, i18n } = useTranslation("accessConsole");
  const user = useUserStore((s) => s.user) as { id?: string; uid?: string; email?: string } | null;
  const storageKey = cameraWallStorageKey(user?.id || user?.uid || user?.email);
  const now = useTicker();

  const camerasQ = useAcList("cameras", undefined, { refetchInterval: 60_000 });
  const sitesQ = useAcList("sites");
  const viewsQ = useAcList("views");
  const viewMut = useAcMutations("views");

  const [layout, setLayoutState] = useState<GridLayout>(() => readWallPrefs(storageKey).layout);
  useEffect(() => setLayoutState(readWallPrefs(storageKey).layout), [storageKey]);
  const setLayout = (l: GridLayout) => {
    setLayoutState(l);
    setMaximized(null);
    writeWallPrefs(storageKey, { layout: l });
  };

  const [siteFilter, setSiteFilter] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [assigned, setAssigned] = useState<(string | null)[]>([]);
  const [activeView, setActiveView] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(0);
  const [maximized, setMaximized] = useState<number | null>(null);
  const [panelOpen, setPanelOpen] = useState(true);
  /** Sólo en móvil: el árbol va arriba del mosaico y empieza plegado. */
  const [treeOpen, setTreeOpen] = useState(false);

  const cameras = useMemo(() => camerasQ.data ?? [], [camerasQ.data]);
  const byId = useMemo(() => new Map(cameras.map((c) => [c.id, c])), [cameras]);
  const filtered = useMemo(() => (siteFilter ? cameras.filter((c) => c.site_id === siteFilter) : cameras), [cameras, siteFilter]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / layout));

  // Paginación automática: cada página llena el mosaico con las siguientes cámaras.
  const fillPage = useCallback(
    (p: number) => {
      setActiveView(null);
      setAssigned(filtered.slice(p * layout, p * layout + layout).map((c) => c.id));
    },
    [filtered, layout],
  );
  // Sólo se vuelve a llenar si cambia la página, el filtro o el mosaico (o al
  // llegar las cámaras por primera vez): un refetch NO borra lo que se colocó a mano.
  const loaded = cameras.length > 0;
  useEffect(() => {
    if (activeView) return;
    const p = Math.min(page, pageCount - 1);
    if (p !== page) setPage(p);
    fillPage(p);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteFilter, layout, page, loaded]);

  // Stream sólo para la celda que ocupa todo el mosaico (maximizada o mosaico de 1).
  const bigIndex = maximized ?? (layout === 1 ? 0 : null);
  const bigCam = bigIndex !== null && assigned[bigIndex] ? byId.get(assigned[bigIndex] as string) : undefined;
  const streamQ = useCameraStream(bigCam?.id, Boolean(bigCam?.has_stream));
  const playable = streamQ.data && /^https:\/\//.test(streamQ.data) ? streamQ.data : null;

  const slots: (CameraTileSource | null)[] = Array.from({ length: layout }, (_, i) => {
    const id = assigned[i];
    const cam = id ? byId.get(id) : undefined;
    if (!cam) return null;
    const src = toTileSource(cam);
    return i === bigIndex && playable ? { ...src, streamUrl: playable } : src;
  });

  const selectedCam = selected !== null && assigned[selected] ? byId.get(assigned[selected] as string) ?? null : null;

  const placeCamera = (index: number, cameraId: string) => {
    setAssigned((prev) => {
      const next = Array.from({ length: layout }, (_, i) => prev[i] ?? null);
      next[index] = cameraId;
      return next;
    });
    setSelected(index);
  };

  /** Doble clic en el árbol: a la celda seleccionada o a la primera libre. */
  const sendToWall = (cameraId: string) => {
    const free = assigned.findIndex((id, i) => i < layout && !id);
    const target = selected ?? (free >= 0 ? free : 0);
    placeCamera(target, cameraId);
  };

  const loadView = (v: AcView) => {
    const l = (GRID_LAYOUTS as readonly number[]).includes(v.layout) ? (v.layout as GridLayout) : 4;
    setLayoutState(l);
    writeWallPrefs(storageKey, { layout: l });
    setActiveView(v.id);
    setMaximized(null);
    setAssigned(Array.from({ length: l }, (_, i) => v.camera_ids[i] ?? null));
    setSelected(0);
  };

  const saveView = async () => {
    const name = window.prompt(t("cameras.saveViewPrompt"), t("cameras.saveViewDefault", { interpolation: { escapeValue: false }, n: (viewsQ.data?.length ?? 0) + 1 }));
    if (!name?.trim()) return;
    try {
      const created = await viewMut.create.mutateAsync({ name: name.trim(), layout, camera_ids: assigned.slice(0, layout).filter(Boolean) as string[], site_id: siteFilter });
      setActiveView(created.id);
      toast.success(t("cameras.viewSaved"));
    } catch {
      toast.error(t("common.saveError"));
    }
  };

  const online = cameras.filter((c) => c.status === "online" || c.status === "recording").length;
  const offline = cameras.filter((c) => c.status === "offline").length;

  const groups: TreeGroup[] = useMemo(() => {
    const sites = sitesQ.data ?? [];
    const known = new Set(sites.map((s) => s.id));
    const gs: TreeGroup[] = sites.map((s) => ({
      id: s.id,
      label: s.name,
      icon: SITE_ICON[s.type ?? "facility"] ?? SITE_ICON.facility,
      items: cameras
        .filter((c) => c.site_id === s.id)
        .map((c) => ({ id: c.id, label: c.name.replace(/^[^—]+—\s*/, ""), icon: CAMERA_ICON, tone: toneOf(CAMERA_TONE, c.status), meta: c.zone, draggableMime: CAMERA_DRAG_MIME })),
    }));
    const orphans = cameras.filter((c) => !known.has(c.site_id));
    if (orphans.length) gs.push({ id: "__none", label: t("common.noSite"), icon: "ri-map-pin-line", items: orphans.map((c) => ({ id: c.id, label: c.name, icon: CAMERA_ICON, tone: toneOf(CAMERA_TONE, c.status), draggableMime: CAMERA_DRAG_MIME })) });
    return gs;
  }, [sitesQ.data, cameras, t]);

  return (
    // Ocupa el alto del área de contenido (NuvexAccessLayout la fija a la ventana) y
    // nunca crece: el árbol hace scroll por su cuenta y el mosaico toma el alto libre.
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden bg-app" data-testid="camera-wall" data-fit="parent">
      {/* Barra superior */}
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-3 py-2">
        <div className="mr-2 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500/15 text-primary-500">
            <i className={CAMERA_ICON} />
          </span>
          <div>
            <h1 className="text-sm font-bold leading-tight text-content">{t("cameras.title")}</h1>
            <p className="text-[11px] text-content-secondary">{t("cameras.subtitle")}</p>
          </div>
        </div>
        <StatusChip tone="success">{t("cameras.onlineCount", { interpolation: { escapeValue: false }, count: online })}</StatusChip>
        <StatusChip tone="neutral">{t("cameras.offlineCount", { interpolation: { escapeValue: false }, count: offline })}</StatusChip>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div role="radiogroup" aria-label={t("cameras.layout")} className="inline-flex overflow-hidden rounded-lg border border-line bg-card">
            {GRID_LAYOUTS.map((l) => (
              <button
                key={l}
                type="button"
                role="radio"
                aria-checked={layout === l}
                title={t("cameras.layoutN", { interpolation: { escapeValue: false }, n: l })}
                onClick={() => setLayout(l)}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-sm ${layout === l ? "bg-primary-500 text-on-primary" : "text-content-secondary hover:bg-subtle"}`}
              >
                <LayoutIcon layout={l} />
                <span className="tabular-nums">{l}</span>
              </button>
            ))}
          </div>
          {!activeView && (
            <div className="flex items-center gap-1 rounded-lg border border-line bg-card px-1 py-0.5 text-sm">
              <button type="button" aria-label={t("common.prevPage")} disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="rounded p-1 text-content-secondary hover:bg-subtle disabled:opacity-40">
                <i className="ri-arrow-left-s-line" />
              </button>
              <span className="px-1 tabular-nums text-content-secondary">{t("common.pageOf", { interpolation: { escapeValue: false }, page: page + 1, total: pageCount })}</span>
              <button type="button" aria-label={t("common.nextPage")} disabled={page >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} className="rounded p-1 text-content-secondary hover:bg-subtle disabled:opacity-40">
                <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          )}
          {activeView && (
            <ToolButton icon="ri-layout-grid-line" onClick={() => { setActiveView(null); fillPage(page); }}>
              {t("cameras.exitView")}
            </ToolButton>
          )}
          <ToolButton icon="ri-save-3-line" onClick={saveView} disabled={viewMut.create.isPending}>
            {t("cameras.saveView")}
          </ToolButton>
          <ToolButton icon="ri-delete-back-2-line" onClick={() => { setAssigned(Array(layout).fill(null)); setActiveView("__custom"); }}>
            {t("cameras.clearWall")}
          </ToolButton>
          <ToolButton icon={panelOpen ? "ri-layout-right-2-line" : "ri-layout-right-line"} onClick={() => setPanelOpen((o) => !o)} title={t("cameras.togglePanel")} />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* Móvil: el árbol va arriba y se pliega para dejarle el alto al mosaico */}
        <button
          type="button"
          aria-expanded={treeOpen}
          aria-controls="camera-wall-tree"
          onClick={() => setTreeOpen((o) => !o)}
          className="flex shrink-0 items-center gap-2 border-b border-line bg-surface px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-muted md:hidden"
        >
          <i className="ri-node-tree" aria-hidden="true" />
          <span className="flex-1">{t("cameras.tree")}</span>
          <span className="tabular-nums">{cameras.length}</span>
          <i className={treeOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} aria-hidden="true" />
        </button>
        {/* Árbol + vistas: columna con scroll propio; nunca estira la pantalla */}
        <div
          id="camera-wall-tree"
          data-testid="camera-wall-tree"
          data-mobile-collapsed={treeOpen ? "false" : "true"}
          className={`${treeOpen ? "flex" : "hidden"} max-h-[45%] min-h-0 shrink-0 flex-col overflow-hidden border-b border-line md:flex md:max-h-none md:w-64 md:border-b-0 md:border-r`}
        >
          <SiteTree
            className="min-h-0 flex-1"
            title={t("cameras.tree")}
            groups={groups}
            selectedGroup={siteFilter}
            selectedItem={selectedCam?.id ?? null}
            onSelectGroup={(id) => { setSiteFilter(id); setPage(0); setActiveView(null); }}
            onSelectItem={(id) => {
              const idx = assigned.indexOf(id);
              if (idx >= 0) setSelected(idx);
            }}
            onActivateItem={sendToWall}
          />
          <div className="max-h-[40%] overflow-y-auto border-t border-line bg-surface">
            <div className="flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wide text-content-muted">
              <span>{t("cameras.views")}</span>
              <span className="tabular-nums">{viewsQ.data?.length ?? 0}</span>
            </div>
            {(viewsQ.data ?? []).map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => loadView(v)}
                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${activeView === v.id ? "bg-primary-500/15 text-primary-500" : "text-content-secondary hover:bg-subtle hover:text-content"}`}
              >
                <LayoutIcon layout={v.layout} />
                <span className="min-w-0 flex-1 truncate">{v.name}</span>
                <span className="text-[10px] tabular-nums text-content-faint">{v.camera_ids.length}</span>
              </button>
            ))}
            <p className="px-3 pb-3 pt-1 text-[11px] text-content-faint">{t("cameras.dragHint")}</p>
          </div>
        </div>

        {/* Mosaico */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-neutral-950 p-1" data-testid="camera-wall-stage">
          <LoadState loading={camerasQ.isLoading} error={camerasQ.error} empty={!cameras.length} emptyText={t("cameras.empty")} onRetry={() => camerasQ.refetch()}>
            {/* El mosaico 16:9 ocupa todo el alto libre (o todo el ancho, lo que alcance primero) */}
            <AspectFit>
              <CameraGrid
                className="h-full w-full"
                layout={layout}
                slots={slots}
                selectedIndex={selected}
                maximizedIndex={maximized}
                onSelect={setSelected}
                onToggleMaximize={(i) => setMaximized((m) => (m === i ? null : i))}
                onDropCamera={(i, id) => { if (!activeView) setActiveView("__custom"); placeCamera(i, id); }}
                onClear={(i) => { setActiveView((v) => v ?? "__custom"); setAssigned((prev) => prev.map((x, j) => (j === i ? null : x))); }}
              />
            </AspectFit>
          </LoadState>
          <p className="shrink-0 px-1 pt-1 text-[11px] text-white/40">{t("cameras.gridHint")}</p>
        </div>

        {/* Ficha de la cámara */}
        {panelOpen && (
          <aside className="hidden w-72 shrink-0 flex-col overflow-y-auto border-l border-line bg-surface xl:flex">
            {selectedCam ? (
              <div className="p-3">
                <div className="mb-2 overflow-hidden rounded-lg border border-line bg-black">
                  {selectedCam.thumbnail_url && <img src={selectedCam.thumbnail_url} alt={selectedCam.name} className="aspect-video w-full object-cover" />}
                </div>
                <h2 className="text-sm font-semibold text-content">{selectedCam.name}</h2>
                <div className="mb-2 mt-1">
                  <StatusChip tone={toneOf(CAMERA_TONE, selectedCam.status)} pulse={selectedCam.status === "recording"}>
                    {t(`video.state.${selectedCam.status}`)}
                  </StatusChip>
                </div>
                <DetailRow label={t("fields.site")}>{selectedCam.site_name ?? "—"}</DetailRow>
                <DetailRow label={t("fields.zone")}>{selectedCam.zone ?? "—"}</DetailRow>
                <DetailRow label={t("fields.model")}>{[selectedCam.brand, selectedCam.model].filter(Boolean).join(" ")}</DetailRow>
                <DetailRow label={t("fields.type")}>{t(`cameraType.${selectedCam.type ?? "dome"}`)}</DetailRow>
                <DetailRow label={t("fields.ip")}><span className="font-mono">{selectedCam.ip_masked ?? "—"}</span></DetailRow>
                <DetailRow label={t("fields.channel")}>{selectedCam.nvr_name ? `${selectedCam.nvr_name} · ` : ""}CH{String(selectedCam.channel ?? 1).padStart(2, "0")}</DetailRow>
                <DetailRow label={t("fields.resolution")}>{selectedCam.resolution} · {selectedCam.fps} fps</DetailRow>
                <DetailRow label={t("fields.features")}>
                  <span className="inline-flex gap-1.5 text-content-secondary">
                    {selectedCam.ptz && <span title="PTZ">PTZ</span>}
                    {selectedCam.night_vision && <i className="ri-moon-line" title={t("fields.nightVision")} />}
                    {selectedCam.audio && <i className="ri-mic-line" title={t("fields.audio")} />}
                    {selectedCam.recording && <i className="ri-record-circle-line text-error" title={t("fields.recording")} />}
                  </span>
                </DetailRow>
                <DetailRow label={t("fields.lastSeen")}>{relativeTime(selectedCam.last_seen_at, i18n.language, now)}</DetailRow>
                <DetailRow label={t("fields.stream")}>{selectedCam.has_stream ? t("cameras.streamConfigured") : t("cameras.streamMissing")}</DetailRow>
              </div>
            ) : (
              <p className="p-4 text-center text-sm text-content-muted">{t("cameras.selectHint")}</p>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

/** Pictograma del mosaico (como los botones de división de iVMS-4200). */
export function LayoutIcon({ layout }: { layout: number }) {
  const cells =
    layout === 1
      ? [[0, 0, 4, 4]]
      : layout === 4
        ? [[0, 0, 2, 2], [2, 0, 2, 2], [0, 2, 2, 2], [2, 2, 2, 2]]
        : layout === 8
          ? [[0, 0, 3, 3], [3, 0, 1, 1], [3, 1, 1, 1], [3, 2, 1, 1], [0, 3, 1, 1], [1, 3, 1, 1], [2, 3, 1, 1], [3, 3, 1, 1]]
          : Array.from({ length: 12 }, (_, i) => [(i % 4), Math.floor(i / 4) * (4 / 3), 1, 4 / 3]);
  return (
    <svg viewBox="0 0 4 4" className="h-3.5 w-3.5" aria-hidden="true">
      {cells.map(([x, y, w, h], i) => (
        <rect key={i} x={x + 0.15} y={y + 0.15} width={w - 0.3} height={h - 0.3} rx={0.2} fill="currentColor" opacity={0.85} />
      ))}
    </svg>
  );
}
